import React, { useCallback, useRef } from "react";
import afterGivingBirth from "../../images/main/afterGivingBirth.png";
import { X } from "lucide-react";
import html2canvas from "html2canvas";

function BabyCongratsModal({
  week,
  babyName = "아기가",
  headline = "나는 정말 행복해요!",
  subtext,
  fromText = "from. Dplanit moming",
  onClose,
  onSave,
  onShare,
}) {
  const modalRef = useRef(null);
  const contentRef = useRef(null);

  // 커스텀 스크롤바 스타일
  const scrollbarStyle = {
    scrollbarWidth: "thin",
    scrollbarColor: "rgba(255, 255, 255, 0.3) transparent",
  };

  const captureModal = useCallback(async () => {
    if (!modalRef.current || !contentRef.current) return null;

    try {
      const originalModal = modalRef.current;
      const clonedModal = originalModal.cloneNode(true);

      clonedModal.style.position = "fixed";
      clonedModal.style.top = "-9999px";
      clonedModal.style.left = "-9999px";
      clonedModal.style.zIndex = "-1";
      clonedModal.style.visibility = "hidden";

      document.body.appendChild(clonedModal);

      const clonedContent = clonedModal.querySelector(".flex-1");

      if (clonedContent) {
        const contentScrollHeight = clonedContent.scrollHeight;
        const buttonsHeight = 90;
        const topPadding = 32;
        const totalHeight = contentScrollHeight + buttonsHeight + topPadding;

        clonedModal.style.height = `${totalHeight}px`;
        clonedModal.style.maxHeight = "none";
        clonedContent.style.overflow = "visible";
        clonedModal.style.visibility = "visible"; 

        await new Promise((resolve) => setTimeout(resolve, 100));

        const canvas = await html2canvas(clonedModal, {
          backgroundColor: "#FEC009",
          scale: 2,
          logging: false,
          useCORS: true,
          allowTaint: true,
          scrollX: 0,
          scrollY: 0,
        });

        document.body.removeChild(clonedModal);

        return canvas;
      } else {
        document.body.removeChild(clonedModal);
        throw new Error("복제된 콘텐츠를 찾을 수 없습니다.");
      }
    } catch (error) {
      console.error("캡처 실패:", error);

      const clonedElements = document.querySelectorAll('[style*="-9999px"]');
      clonedElements.forEach((el) => {
        if (el.parentNode) {
          el.parentNode.removeChild(el);
        }
      });

      return null;
    }
  }, []);

  const handleSave = useCallback(async () => {
    const canvas = await captureModal();
    if (!canvas) {
      alert("이미지 저장에 실패했습니다.");
      return;
    }

    try {
      canvas.toBlob((blob) => {
        if (!blob) {
          alert("이미지 생성에 실패했습니다.");
          return;
        }

        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${babyName}_${week}주차_축하.png`;

        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, "image/png");

      if (onSave) {
        onSave();
      }
    } catch (error) {
      console.error("저장 실패:", error);
      alert("이미지 저장에 실패했습니다.");
    }
  }, [captureModal, babyName, week, onSave]);

  const handleShare = useCallback(async () => {
    const canvas = await captureModal();
    if (!canvas) {
      alert("이미지 생성에 실패했습니다.");
      return;
    }

    try {
      canvas.toBlob(async (blob) => {
        if (!blob) {
          alert("이미지 생성에 실패했습니다.");
          return;
        }

        try {
          if (navigator.clipboard && navigator.clipboard.write) {
            const item = new ClipboardItem({ "image/png": blob });
            await navigator.clipboard.write([item]);
            alert("이미지가 클립보드에 복사되었습니다!");
          } else {
            throw new Error("클립보드 API 미지원");
          }
        } catch (clipboardError) {
          console.error("클립보드 복사 실패:", clipboardError);
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `${babyName}_${week}주차_축하.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          alert("클립보드 복사가 지원되지 않아 이미지를 다운로드했습니다!");
        }
      }, "image/png");

      if (onShare) {
        onShare();
      }
    } catch (error) {
      console.error("공유 실패:", error);
      alert("공유에 실패했습니다.");
    }
  }, [captureModal, babyName, week, onShare]);

  return (
    <>
      <style>
        {`
          .custom-scrollbar::-webkit-scrollbar {
            width: 6px;
          }
          
          .custom-scrollbar::-webkit-scrollbar-track {
            background: transparent;
            border-radius: 3px;
          }
          
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: rgba(255, 255, 255, 0.3);
            border-radius: 3px;
            transition: background 0.2s ease;
          }
          
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: rgba(255, 255, 255, 0.5);
          }
          
          .custom-scrollbar::-webkit-scrollbar-thumb:active {
            background: rgba(255, 255, 255, 0.7);
          }
        `}
      </style>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
        <div
          ref={modalRef}
          className="relative w-[320px] md:w-[350px] h-[530px] max-w-full rounded-[20px] bg-[#FEC009] shadow-2xl flex flex-col"
        >
          <button
            aria-label="닫기"
            onClick={onClose}
            className="absolute top-4 right-5 text-white text-[26px] z-10"
          >
            <X className="w-6 h-6" />
          </button>

          <div
            ref={contentRef}
            className="flex-1 overflow-y-auto px-5 pt-[29px] pb-4 custom-scrollbar mt-3"
            style={scrollbarStyle}
          >
            <h2 className="text-white text-[22px] font-semibold text-center mb-6">
              {headline}
            </h2>

            <div className="mb-4">
              <img
                src={afterGivingBirth}
                alt="아기와 기린 일러스트"
                width={200}
                height={150}
                className="mx-auto select-none"
                style={{ objectFit: "contain" }}
              />
            </div>

            <h3 className="text-white text-[21px] font-semibold text-center mb-4">
              {babyName}(이)가 {week}주가 되었어요!
            </h3>

            <div className="text-white text-[14px] whitespace-pre-line mb-4">
              {subtext}
            </div>

            <div className="text-white text-[14px] text-center opacity-80">
              {fromText}
            </div>
          </div>

          <div className="flex gap-4 px-6 pb-6 pt-3">
            <button
              onClick={handleSave}
              className="flex-1 rounded-full bg-[#FF7D3B] text-white text-[15px] font-bold py-4 transition"
            >
              저장하기
            </button>
            <button
              onClick={handleShare}
              className="flex-1 rounded-full bg-[#FF7D3B] text-white text-[15px] font-bold py-4 transition"
            >
              공유하기
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default BabyCongratsModal;
